import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Paper,
  Typography,
  Stack,
  Box,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Alert,
  CircularProgress,
} from "@mui/material";
import { API_BASE_URL } from "../config";
import { ACTIVE_ATTENDANCE_STATUSES, DECLARATIONS, STATUS_COLOR } from "../constants";
import { useMyPersonnel } from "../useMyPersonnel";
import MyEventDialog from "../components/MyEventDialog";
import MyContractDialog from "../components/MyContractDialog";
import AvailabilityCard from "../components/AvailabilityCard";
import { CONTRACTS_API, fmtDateTime } from "../contracts";

const ATT_API = `${API_BASE_URL}/attendances`;
const READINESS_API = `${API_BASE_URL}/personnel/self/readiness`;

// What the record still needs, as a sentence and a way to fix it. Every gap is
// something the person can fill in on My details (core/personnel_readiness.py).
function MissingDetails({ gaps, what }) {
  const navigate = useNavigate();
  if (!gaps?.length) return null;
  return (
    <Alert severity="warning"
           action={<Button color="inherit" size="small" onClick={() => navigate("/my-details?edit=1")}>My details</Button>}>
      Before you can {what}, please add these to My details: {gaps.join("; ")}.
    </Alert>
  );
}

// Local date-only formatter (avoids UTC off-by-one for "YYYY-MM-DD").
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y) return "—";
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric",
  });
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const dateRange = (e) => {
  if (e.start_date && e.end_date) return `${fmtDate(e.start_date)} – ${fmtDate(e.end_date)}`;
  return fmtDate(e.start_date || e.end_date);
};

function EventTable({ rows, empty, onOpen }) {
  return (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
              <TableCell>Event</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Event role</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((a) => (
              <TableRow hover key={a.id} onClick={() => onOpen(a)} sx={{ cursor: "pointer" }}>
                <TableCell>{a.event.label_long || a.event.name || `Event #${a.event_id}`}</TableCell>
                <TableCell>{dateRange(a.event)}</TableCell>
                <TableCell>{a.role || "—"}</TableCell>
                <TableCell>
                  {a.status
                    ? <Chip size="small" label={a.status} color={STATUS_COLOR[a.status] || "default"} />
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 3 }}>
                  {empty}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function MyEvents() {
  const { loading, person, hasIdentity, email } = useMyPersonnel();
  const [attendances, setAttendances] = useState([]);
  const [attLoading, setAttLoading] = useState(false);
  const [selected, setSelected] = useState(null); // attendance shown in the detail dialog
  // Engagement schedules: waiting for this person's signature, or signed.
  const [contracts, setContracts] = useState([]);
  const [signing, setSigning] = useState(null);
  // Events taking availability, and what the record lacks for that or a contract.
  const [availability, setAvailability] = useState([]);
  const [readiness, setReadiness] = useState({ availability: [], contract: [] });
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!person?.id) { setAttendances([]); return; }
    setAttLoading(true);
    axios.get(ATT_API + "/", { params: { personnel_id: person.id } })
      .then(res => setAttendances(res.data))
      .finally(() => setAttLoading(false));
  }, [person?.id]);

  useEffect(() => {
    if (!person?.id) return;
    axios.get(`${ATT_API}/availability`).then(res => setAvailability(res.data)).catch(() => {});
    axios.get(READINESS_API).then(res => setReadiness(res.data)).catch(() => {});
  }, [person?.id]);

  const availabilitySaved = (row) => {
    setAvailability(rows => rows.map(r => (r.event.id === row.event.id ? row : r)));
    // Saying available can make an attendance; keep the tables below in step.
    axios.get(ATT_API + "/", { params: { personnel_id: person.id } })
      .then(res => setAttendances(res.data)).catch(() => {});
  };

  const loadContracts = () => axios.get(`${CONTRACTS_API}/mine`)
    .then(res => { setContracts(res.data || []); return res.data || []; })
    .catch(() => []);

  // The email asking someone to sign links to ?contract=<id>: open it straight away.
  useEffect(() => {
    if (!person?.id) return;
    const wanted = Number(searchParams.get("contract"));
    loadContracts().then(list => {
      if (!wanted) return;
      const found = list.find(c => c.id === wanted);
      if (found) setSigning(found);
      searchParams.delete("contract");
      setSearchParams(searchParams, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person?.id]);

  if (loading) {
    return <Stack sx={{ alignItems: "center", py: 6 }}><CircularProgress /></Stack>;
  }

  if (!hasIdentity) {
    return (
      <Stack spacing={3}>
        <Typography variant="h4">My events</Typography>
        <Alert severity="info">You are not signed in, so there are no events to show.</Alert>
      </Stack>
    );
  }

  if (!person) {
    return (
      <Stack spacing={3}>
        <Typography variant="h4">My events</Typography>
        <Alert severity="warning">
          We couldn't find a Personnel record matching your account
          {email ? ` (${email})` : ""}, so we can't show your events.
        </Alert>
      </Stack>
    );
  }

  const today = todayISO();
  // Only events the person is committed to — being flagged Available (or having
  // no status yet) isn't a booking, so those are left out entirely.
  const withEvent = attendances.filter(
    a => a.event && ACTIVE_ATTENDANCE_STATUSES.includes(a.status));
  // An event is "past" once its end date (or start date, if undated end) is
  // before today; undated events are treated as upcoming.
  const dateKey = (e) => e.end_date || e.start_date || "";
  const isPast = (a) => { const k = dateKey(a.event); return k && k < today; };

  const upcoming = withEvent.filter(a => !isPast(a))
    .sort((a, b) => (a.event.start_date || "￿").localeCompare(b.event.start_date || "￿"));
  const past = withEvent.filter(isPast)
    .sort((a, b) => (b.event.start_date || "").localeCompare(a.event.start_date || ""));

  const pending = contracts.filter(c => c.status === "sent");

  return (
    <Stack spacing={3}>
      <Typography variant="h4">My events</Typography>

      {pending.length > 0 && (
        <Alert severity="warning" variant="outlined">
          <Typography variant="subtitle2" gutterBottom>Waiting for your signature</Typography>
          <Stack spacing={1}>
            {pending.map(c => (
              <Box key={c.id} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
                  {c.documents[0]?.title || "Engagement schedule"} — {c.event_label}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {" "}(sent {fmtDateTime(c.sent_at)})
                  </Typography>
                </Typography>
                <Button size="small" variant="contained" onClick={() => setSigning(c)}>Review and sign</Button>
              </Box>
            ))}
          </Stack>
        </Alert>
      )}
      {pending.length > 0 && <MissingDetails gaps={readiness.contract} what="sign your contract" />}

      {availability.length > 0 && (
        <Box>
          <Typography variant="h6">Taking availability</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Let us know which of these events you could work. Saying you're available isn't a
            booking — we'll be in touch if we'd like you on the team.
          </Typography>
          <Stack spacing={1.5}>
            <MissingDetails gaps={readiness.availability} what="say you're available" />
            {/* Made once, on My details, and standing for every event — so a
                reminder of them each time someone puts themselves forward. */}
            {DECLARATIONS.every(d => person[d.field]) && (
              <Alert severity="info">
                You confirmed your declarations of suitability (right to work, safeguarding and
                suitability, fitness to practise) on{" "}
                {[...new Set(DECLARATIONS.map(d => fmtDate(person[d.field])))].join(" and ")}.
                If anything has changed, please let us know at hello@shelter-stone.com.
              </Alert>
            )}
            {availability.map(row => (
              <AvailabilityCard key={row.event.id} row={row} dateRange={dateRange}
                                blocked={readiness.availability.length > 0}
                                onSaved={availabilitySaved} />
            ))}
          </Stack>
        </Box>
      )}

      {attLoading ? (
        <Stack sx={{ alignItems: "center", py: 4 }}><CircularProgress /></Stack>
      ) : (
        <>
          <Box>
            <Typography variant="h6" gutterBottom>Upcoming</Typography>
            <EventTable rows={upcoming} empty="You have no upcoming events." onOpen={setSelected} />
          </Box>
          <Box>
            <Typography variant="h6" gutterBottom>Past</Typography>
            <EventTable rows={past} empty="No past events." onOpen={setSelected} />
          </Box>
        </>
      )}

      <MyEventDialog attendance={selected} dateRange={dateRange} onClose={() => setSelected(null)}
                     contracts={contracts.filter(c => c.attendance_id === selected?.id)}
                     onOpenContract={setSigning} />
      <MyContractDialog contract={signing} onClose={() => setSigning(null)}
                        blockers={readiness.contract}
                        onSigned={() => {
                          loadContracts();
                          // Signing moves the attendance to Contract Signed.
                          axios.get(ATT_API + "/", { params: { personnel_id: person.id } })
                            .then(res => setAttendances(res.data)).catch(() => {});
                        }} />
    </Stack>
  );
}
